/**
 * @description
 * This is the main pagination block, that bundles pagination
 * features together
 *
 */

import React from 'react';
import styled from '@cthings.co/styled-components';
import { PaginationNumbering } from '../paginationNumbering/PaginationNumbering';
import { useTableContext } from '../../../context';
import { media } from '@cthings.co/styles-utils';
import { useMediaType } from '@cthings.co/styles-utils';

import { colorFetchFDS as colorFetch } from '@cthings.co/styles-utils';
import { useTheme } from '@cthings.co/styled-components';

export enum PaginationType {
  BASIC = 'BASIC',
  MANAGE = 'MANAGE',
}

type WrapperProps = {
  editInProcess?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  background-color: ${colorFetch('white')};
  box-sizing: border-box;
  padding: 13px 24px;
  border-radius: 0 0 8px 8px;
  border-top: 1px solid;
  border-color: ${colorFetch('gray4')};
  opacity: ${({ editInProcess }) => (editInProcess ? '0.5' : '1')};
  ${media.phone} {
    padding: 13px 16px;
  }
`;

type MarkProps = {
  editInProcess?: boolean;
};

// @TODO fix media
const Mark = styled.span<MarkProps>`
  display: inline-block;
  color: ${colorFetch('primary')};
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  user-select: none;
  margin-right: 10px;
  &.showing {
    color: ${colorFetch('gray1')};
  }
  &.small {
    color: ${colorFetch('gray1')};
    /* display: none; */
  }
  &.full {
    display: inline-block;
  }
  &.sliced {
    display: none;
  }
  ${media.tablet} {
    &.sliced {
      display: inline-block;
    }
    &:hover.sliced {
      cursor: ${({ editInProcess }: any) => (editInProcess ? 'auto' : 'pointer')};
      color: ${colorFetch('gray2')};
      transition: color 0.3s ease;
    }
  }
  ${media.phoneS} {
    display: none;
    display: inline-block;
  }
`;

const BoldSpan = styled.span`
  color: ${colorFetch('black')};
  font-weight: bold;
`;

const ShowingSpan = styled.span`
  color: ${colorFetch('gray1')};
  display: inline;
  ${media.phone} {
    display: none;
  }
`;

export const Pagination = ({
  onPageChange,
  editInProcess,
  languageStrings,
  ...props
}: {
  onPageChange: (value: number, isOffset?: boolean) => void;
  editInProcess?: boolean;
  languageStrings?: any;
  className?: string;
}) => {
  const [{ pagination, globalProps }] = useTableContext();
  const { total_items, page_number: page } = pagination;
  const page_number = page - 1;
  const { pageSize } = globalProps;
  const pagesNum = Math.ceil(total_items / pageSize);
  const startRange = total_items > 0 ? page_number * pageSize + 1 : 0;
  const endRange = (page_number + 1) * pageSize;
  const groundDelta =
    total_items > 0
      ? Math.floor(endRange / total_items)
        ? (endRange + pageSize) % (total_items + pageSize)
        : 0
      : pageSize;
  const limitedEndRange = endRange - groundDelta;
  const theme = useTheme();
  const paginationOnChange = editInProcess ? () => {} : onPageChange;

  const { tablet } = useMediaType();

  return (
    <Wrapper editInProcess={editInProcess} {...props}>
      {!tablet ? (
        <Mark className="showing" theme={theme}>
          <ShowingSpan>{languageStrings.problemsShowing}</ShowingSpan>{' '}
          <BoldSpan>{`${startRange}-${limitedEndRange}`}</BoldSpan> {languageStrings.problemsOf}{' '}
          <BoldSpan className="total_items">{total_items}</BoldSpan>
        </Mark>
      ) : (
        <Mark className="small" theme={theme} editInProcess={editInProcess}>
          <BoldSpan>{`${startRange}-${limitedEndRange}`}</BoldSpan> {languageStrings.problemsOf}{' '}
          <BoldSpan className="total_items">{total_items}</BoldSpan>
        </Mark>
      )}
      <PaginationNumbering
        currentPage={page_number}
        onPageChange={paginationOnChange}
        pagesNum={pagesNum}
        editInProcess={editInProcess}
        languageStrings={languageStrings}
      />
    </Wrapper>
  );
};
